'use client'

import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react'
import { useRouter } from 'next/navigation'
import { Search, CornerDownLeft, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFocusTrap } from '@/lib/use-focus-trap'
import type { HelpSearchDoc } from '@/lib/help-nav'

export type HelpSearchHandle = { open: () => void }

type Result = HelpSearchDoc & { score: number }

// Lightweight in-memory search over the prebuilt index (title +
// summary + body text). No new deps: a scored substring match. The
// index is passed in from the server (built once from the article
// data) so the client just filters it.
function searchIndex(index: HelpSearchDoc[], query: string): Result[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const terms = q.split(/\s+/).filter(Boolean)
  const results: Result[] = []
  for (const doc of index) {
    const title = doc.title.toLowerCase()
    const summary = doc.summary.toLowerCase()
    let score = 0
    let matchedAll = true
    for (const t of terms) {
      let termScore = 0
      if (title.includes(t)) termScore += title.startsWith(t) ? 12 : 8
      if (summary.includes(t)) termScore += 4
      if (doc.categoryTitle.toLowerCase().includes(t)) termScore += 3
      if (doc.text.includes(t)) termScore += 1
      if (termScore === 0) matchedAll = false
      score += termScore
    }
    if (matchedAll && score > 0) results.push({ ...doc, score })
  }
  results.sort((a, b) => b.score - a.score)
  return results.slice(0, 8)
}

export const HelpSearch = forwardRef<
  HelpSearchHandle,
  {
    index: HelpSearchDoc[]
    variant?: 'bar' | 'compact'
    // When provided, the search modal offers an "Ask AI" affordance
    // that closes search and hands the current query to the Ask-AI
    // island (a Cmd-K → Ask AI handoff).
    onAskAi?: (query: string) => void
  }
>(function HelpSearch({ index, variant = 'bar', onAskAi }, ref) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  const results = useMemo(() => searchIndex(index, query), [index, query])

  useEffect(() => setActive(0), [query])

  const openSearch = useCallback(() => {
    setOpen(true)
    // Focus after the dialog mounts.
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [])

  const closeSearch = useCallback(() => {
    setOpen(false)
    setQuery('')
  }, [])

  useImperativeHandle(ref, () => ({ open: openSearch }), [openSearch])

  useFocusTrap(overlayRef, open)

  const askAi = useCallback(() => {
    const q = query.trim()
    closeSearch()
    onAskAi?.(q)
  }, [query, closeSearch, onAskAi])

  // Cmd/Ctrl-K to open, Escape to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        openSearch()
      } else if (e.key === 'Escape' && open) {
        closeSearch()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, openSearch, closeSearch])

  const go = useCallback(
    (slug: string) => {
      closeSearch()
      router.push(`/help/${slug}`)
    },
    [router, closeSearch],
  )

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, Math.max(results.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault()
      go(results[active].slug)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openSearch}
        aria-label="Search the help center"
        aria-keyshortcuts="Control+K Meta+K"
        className={cn(
          'group flex w-full items-center gap-2 rounded-md border bg-card text-left text-muted-foreground transition hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
          variant === 'bar' ? 'px-4 py-2.5 text-sm' : 'px-3 py-2 text-sm',
        )}
      >
        <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1">Search guides…</span>
        <kbd className="hidden rounded border bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline-block">
          ⌘K
        </kbd>
      </button>

      {open && (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label="Search the help center"
          className="fixed inset-0 z-[100] flex items-start justify-center bg-foreground/30 px-4 pt-[12vh] backdrop-blur-sm"
          onMouseDown={(e) => {
            if (e.target === overlayRef.current) closeSearch()
          }}
        >
          <div className="w-full max-w-xl overflow-hidden rounded-xl border bg-card shadow-2xl">
            <div className="flex items-center gap-3 border-b px-4">
              <Search className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                type="text"
                role="combobox"
                aria-expanded={results.length > 0}
                aria-controls="help-search-results"
                aria-autocomplete="list"
                placeholder="Search guides…"
                className="w-full bg-transparent py-4 text-sm outline-none placeholder:text-muted-foreground"
              />
              <kbd className="rounded border bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                esc
              </kbd>
            </div>

            {query.trim() && (
              <ul id="help-search-results" role="listbox" className="max-h-[55vh] overflow-y-auto p-2">
                {results.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No guides match “{query}”.
                  </li>
                )}
                {results.map((r, i) => (
                  <li key={r.slug} role="option" aria-selected={i === active}>
                    <button
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(r.slug)}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition',
                        i === active ? 'bg-primary/10' : 'hover:bg-secondary/50',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className={cn('truncate text-sm font-medium', i === active ? 'text-primary' : 'text-foreground')}>
                          {r.title}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">{r.categoryTitle}</div>
                      </div>
                      {i === active && <CornerDownLeft className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {onAskAi && (
              <div className="border-t p-2">
                <button
                  type="button"
                  onClick={askAi}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span className="text-sm text-foreground">
                    {query.trim() ? <>Ask AI: <span className="font-medium">“{query.trim()}”</span></> : 'Ask AI a question'}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
})
